/**
 * POS product catalog — single source of truth for the venue POS
 * (https://ocmenafestival.com/pos) and the Festival HQ pricing view.
 *
 * Prices are in USD cents. `categories` lists every POS station tab the
 * product is sold at; 'ALL' means every station. `tax_exempt` items skip
 * sales tax at checkout (vendor supplies).
 */

export const POS_PRODUCT_CATALOG = [
  // Test products (always shown)
  { id: 'test-50c', name: 'Test Item (50¢)', unit_price_cents: 50, categories: ['ALL', 'PAC AMP A', 'PAC AMP B', 'CARNIVAL', 'WATER BOOTH', 'FIFA'] },
  { id: 'test-1d', name: 'Test Item ($1)', unit_price_cents: 100, categories: ['ALL', 'PAC AMP A', 'PAC AMP B', 'CARNIVAL', 'WATER BOOTH', 'FIFA'] },
  // Real products — sorted alphabetically by name
  { id: 'baklava', name: 'Baklava', unit_price_cents: 1000, categories: ['ALL', 'PAC AMP A', 'PAC AMP B'] },
  { id: 'basboosa', name: 'Basboosa', unit_price_cents: 1000, categories: ['ALL', 'PAC AMP A', 'PAC AMP B'] },
  { id: 'blu-energy', name: 'Blu Energy', unit_price_cents: 300, categories: ['ALL', 'PAC AMP A', 'PAC AMP B', 'CARNIVAL', 'WATER BOOTH', 'FIFA'] },
  { id: 'cotton-candy-cone', name: 'Cotton Candy Cone', unit_price_cents: 500, categories: ['ALL', 'PAC AMP A', 'PAC AMP B', 'CARNIVAL'] },
  { id: 'falafel-box', name: 'Falafel Box', unit_price_cents: 1000, categories: ['ALL', 'PAC AMP A', 'PAC AMP B'] },
  { id: 'hummus', name: 'Hummus', unit_price_cents: 600, categories: ['ALL', 'PAC AMP A', 'PAC AMP B'] },
  { id: 'hummus-with-chips', name: 'Hummus With Chips', unit_price_cents: 1200, categories: ['ALL', 'PAC AMP A', 'PAC AMP B'] },
  { id: 'mini-tahini-jar', name: 'Mini Tahini Jar', unit_price_cents: 100, categories: ['ALL', 'PAC AMP A', 'PAC AMP B'] },
  { id: 'mini-toum-jar', name: 'Mini Toum Jar', unit_price_cents: 100, categories: ['ALL', 'PAC AMP A', 'PAC AMP B'] },
  { id: 'nachos-cheese', name: 'Nachos with Cheese', unit_price_cents: 1000, categories: ['ALL', 'PAC AMP A', 'PAC AMP B', 'FIFA'] },
  { id: 'pita-chips', name: 'Pita Chips', unit_price_cents: 900, categories: ['ALL', 'PAC AMP A', 'PAC AMP B'] },
  { id: 'popcorn', name: 'Popcorn', unit_price_cents: 500, categories: ['ALL', 'PAC AMP A', 'PAC AMP B', 'CARNIVAL', 'FIFA'] },
  { id: 'shawarma-beef', name: 'Shawarma Beef', unit_price_cents: 1500, categories: ['ALL', 'PAC AMP A', 'PAC AMP B'] },
  { id: 'shawarma-chicken', name: 'Shawarma Chicken', unit_price_cents: 1500, categories: ['ALL', 'PAC AMP A', 'PAC AMP B'] },
  { id: 'shawarma-combo', name: 'Shawarma Combo', unit_price_cents: 1650, categories: ['ALL', 'PAC AMP A', 'PAC AMP B'] },
  { id: 'slushy', name: 'Slushy', unit_price_cents: 800, categories: ['ALL', 'PAC AMP A', 'PAC AMP B', 'CARNIVAL'] },
  { id: 'soccer-ball', name: 'Soccer Ball', unit_price_cents: 1500, categories: ['ALL', 'FIFA'] },
  { id: 'soda', name: 'Soda', unit_price_cents: 300, categories: ['ALL', 'PAC AMP A', 'PAC AMP B', 'CARNIVAL', 'WATER BOOTH', 'FIFA'] },
  { id: 'water', name: 'Water', unit_price_cents: 200, categories: ['ALL', 'PAC AMP A', 'PAC AMP B', 'CARNIVAL', 'WATER BOOTH', 'FIFA'] },
  // Ride tickets
  { id: 'ride-ticket-fri', name: 'Ticket - Fri', unit_price_cents: 400, categories: ['ALL', 'RIDE TICKETS'] },
  { id: 'ride-ticket-sat-sun', name: 'Ticket - Sat/Sun', unit_price_cents: 600, categories: ['ALL', 'RIDE TICKETS'] },
  { id: 'ride-wristband-fri', name: 'Unlimited Wristband - Fri', unit_price_cents: 4000, categories: ['ALL', 'RIDE TICKETS'] },
  { id: 'ride-wristband-sat', name: 'Unlimited Wristband - Sat', unit_price_cents: 5000, categories: ['ALL', 'RIDE TICKETS'] },
  { id: 'ride-wristband-sun', name: 'Unlimited Wristband - Sun', unit_price_cents: 5000, categories: ['ALL', 'RIDE TICKETS'] },
  // Vendor (no tax)
  { id: 'vendor-ice', name: 'Ice', unit_price_cents: 1000, categories: ['ALL', 'VENDOR'], tax_exempt: true },
  { id: 'vendor-40lb-ice', name: '40 lb Bag of Ice', unit_price_cents: 2000, categories: ['ALL', 'VENDOR'], tax_exempt: true },
  { id: 'vendor-pack-soda', name: 'Pack of Soda', unit_price_cents: 4800, categories: ['ALL', 'VENDOR'], tax_exempt: true },
  { id: 'vendor-pack-blu-energy', name: 'Pack of Blu Energy', unit_price_cents: 4800, categories: ['ALL', 'VENDOR'], tax_exempt: true },
  { id: 'vendor-pack-water', name: 'Pack of Water', unit_price_cents: 2400, categories: ['ALL', 'VENDOR'], tax_exempt: true },
];

export const POS_TABS = ['ALL', 'PAC AMP A', 'PAC AMP B', 'CARNIVAL', 'WATER BOOTH', 'FIFA', 'RIDE TICKETS', 'VENDOR', 'MANUAL'];

/** Sellable station categories (excludes the ALL / MANUAL pseudo-tabs). */
export const POS_CATEGORIES = POS_TABS.filter((t) => t !== 'ALL' && t !== 'MANUAL');

/** Display metadata per POS category — used by Festival HQ pricing cards. */
export const POS_CATEGORY_META = {
  'PAC AMP A': { blurb: 'Main amphitheater concessions — stand A', tone: 'accent' },
  'PAC AMP B': { blurb: 'Main amphitheater concessions — stand B', tone: 'purple' },
  CARNIVAL: { blurb: 'Carnival & rides area', tone: 'amber' },
  'WATER BOOTH': { blurb: 'Water & beverages booth', tone: 'blue' },
  FIFA: { blurb: 'Watch-party zone', tone: 'green' },
  'RIDE TICKETS': { blurb: 'Single rides & unlimited wristbands', tone: 'red' },
  VENDOR: { blurb: 'Vendor supplies (tax-exempt)', tone: 'slate' },
};

/** Products that belong to a given POS category (ignores the ALL flag). */
export const posProductsForCategory = (category) =>
  POS_PRODUCT_CATALOG
    .filter((p) => p.categories.includes(category))
    .sort((a, b) => a.name.localeCompare(b.name));

export const centsToUsd = (cents) =>
  (Number(cents || 0) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
